// Unter dem Teaser, eingeklappt: Historie, Mapping je Platzhalter und die
// Vorlage als HTML. Mapping und Vorlage wie im FFPV-Flyer (FfpvFlyerBoard.jsx),
// nur mit contract_key 'ffpv-teaser' und im Stil der aufgeraeumten App.
//
// Vorlage und Mapping werden erst beim Aufklappen geladen - die meisten
// Bearbeiter erzeugen nur und brauchen beides nie.

import React, { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, History, Link2, Save, Settings2 } from 'lucide-react';
import { T, feldStil, karteStil, knopfStil } from '../../lib/designTokens';
import { Aufklapper, Ueberzeile, textKnopfStil } from '../contracts/abwaegenAnzeige';
import {
  MAPPING_HINWEISE, PLATZHALTER_INFO, dealKennung, platzhalterAusHtml, zusatzSchluessel,
} from '../../lib/ffpvTeaser';
import { mappingLaden, mappingSpeichern, teaserLesen, teaserSenden } from './ffpvTeaserApi';
import { Fehlerkasten, Laedt, Marke } from './ffpvTeaserSchritte';

const HINWEISLISTE_ID = 'ffpv-teaser-feld-hinweise';

function zeitText(wert) {
  if (!wert) return '';
  const datum = new Date(wert);
  return Number.isNaN(datum.getTime()) ? '' : datum.toLocaleString('de-DE');
}

function aus(gesperrt) {
  return { opacity: gesperrt ? 0.45 : 1, cursor: gesperrt ? 'not-allowed' : 'pointer' };
}

function Abschnitt({ offen, onUmschalten, icon: Icon, titel, zusatz, children }) {
  return (
    <div style={{ borderTop: `1px solid ${T.randWeich}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px' }}>
        <Icon size={14} color={T.muted} />
        <Aufklapper offen={offen} onClick={onUmschalten} farbe={T.body}>{titel}</Aufklapper>
        <span style={{ flex: 1 }} />
        {zusatz}
      </div>
      {offen ? <div role="region" aria-label={titel} style={{ padding: '0 14px 14px' }}>{children}</div> : null}
    </div>
  );
}

function HistorieListe({ historie }) {
  if (historie.fehler) return <Fehlerkasten>{historie.fehler}</Fehlerkasten>;
  if (historie.laedt && !historie.items.length) return <Laedt>Historie wird geladen …</Laedt>;
  if (!historie.items.length) return <div style={{ fontSize: T.tMeta, color: T.muted }}>Noch keine Teaser erstellt.</div>;
  return (
    <div style={{ border: `1px solid ${T.randWeich}`, borderRadius: T.rKarte, overflow: 'hidden' }}>
      {historie.items.map((eintrag, i) => (
        <div key={`${eintrag.dealId}-${eintrag.zeit}-${i}`} style={{
          padding: '8px 12px', borderTop: i === 0 ? 'none' : `1px solid ${T.hairline}`, display: 'grid', gap: 3,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Marke ton={eintrag.ok ? 'gut' : 'gefahr'}>{eintrag.ok ? 'Erstellt' : 'Fehler'}</Marke>
            <span style={{ fontSize: T.tSm, fontWeight: 700, color: T.ink }}>
              {dealKennung(eintrag.dealId)}{eintrag.dealTitle ? ` · ${eintrag.dealTitle}` : ''}
            </span>
            <Marke>{eintrag.trigger === 'webhook' ? 'Automatik' : 'Manuell'}</Marke>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: T.tMikro, color: T.label }}>{zeitText(eintrag.zeit)}</span>
          </div>
          {eintrag.ok && eintrag.fileName ? (
            <div style={{ fontSize: T.tMikro, color: T.muted, wordBreak: 'break-all' }}>{eintrag.fileName}</div>
          ) : null}
          {!eintrag.ok && eintrag.fehler ? (
            <div style={{ fontSize: T.tMikro, color: T.gefahr }}>{eintrag.fehler}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function MappingEditor({ platzhalter, mapping, darfSpeichern, onFeld, onSpeichern }) {
  const zusatz = zusatzSchluessel(mapping.felder, platzhalter);
  const eingabe = (schluessel, istSystem) => (
    <input
      list={HINWEISLISTE_ID}
      value={mapping.felder[schluessel] || ''}
      onChange={(e) => onFeld(schluessel, e.target.value)}
      placeholder={istSystem ? 'leer = Standardquelle, oder z. B. UF_CRM_…' : 'z. B. DEAL.TITLE, UF_CRM_…'}
      disabled={!darfSpeichern}
      aria-label={`Mapping für ${schluessel}`}
      style={{ ...feldStil, padding: '6px 8px', fontSize: T.tXs, fontFamily: 'monospace' }}
    />
  );

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ fontSize: T.tMeta, color: T.muted, lineHeight: 1.5 }}>
        Welches Bitrix-Feld in welchen Platzhalter kommt. Die Standardquellen gelten auch ohne Eintrag; ein
        Eintrag überschreibt sie, ein Nachtrag im Teaser überschreibt beides. Kontakt- und Firmendaten des
        Deals gehören nicht in den Teaser.
      </div>
      {mapping.laedt ? <Laedt>Mapping wird geladen …</Laedt> : null}
      <Fehlerkasten>{mapping.fehler}</Fehlerkasten>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '10px 18px' }}>
        {platzhalter.map((schluessel) => {
          const info = PLATZHALTER_INFO[schluessel];
          return (
            <div key={schluessel} style={{ display: 'grid', gap: 4, alignContent: 'start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <code style={{ fontFamily: 'monospace', fontSize: T.tXs, fontWeight: 700, color: T.ink }}>{`{{${schluessel}}}`}</code>
                {/* Nur das Ungewoehnliche markieren: Fast alle Platzhalter haben
                    eine Standardquelle, eine Marke an jedem sagte nichts. */}
                {info ? null : <Marke ton="warn" titel="Ohne Eintrag bleibt dieser Platzhalter leer.">nur per Mapping</Marke>}
              </div>
              {info ? <div style={{ fontSize: T.tMikro, color: T.muted, lineHeight: 1.4 }}>{info.desc}</div> : null}
              {eingabe(schluessel, Boolean(info))}
            </div>
          );
        })}
      </div>

      {zusatz.length ? (
        <div style={{ display: 'grid', gap: 6, paddingTop: 8, borderTop: `1px dashed ${T.rand}` }}>
          <Ueberzeile>Weitere gespeicherte Zuordnungen ({zusatz.length})</Ueberzeile>
          <div style={{ fontSize: T.tMikro, color: T.muted }}>Stehen nicht in der Vorlage. Bleiben gespeichert, bis du sie leerst.</div>
          {zusatz.map((schluessel) => (
            <div key={schluessel} style={{ display: 'grid', gap: 3 }}>
              <code style={{ fontFamily: 'monospace', fontSize: T.tXs, color: T.body }}>{`{{${schluessel}}}`}</code>
              {eingabe(schluessel, false)}
            </div>
          ))}
        </div>
      ) : null}

      <datalist id={HINWEISLISTE_ID}>
        {MAPPING_HINWEISE.map((hinweis) => <option key={hinweis.value} value={hinweis.value}>{hinweis.label}</option>)}
      </datalist>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ flex: 1, fontSize: T.tMikro, color: T.muted }}>
          {mapping.gespeichertAm ? `Gespeichert: ${zeitText(mapping.gespeichertAm)}` : 'Noch kein Mapping gespeichert.'}
        </span>
        {darfSpeichern ? (
          <button type="button" onClick={onSpeichern} disabled={mapping.speichert} style={{ ...knopfStil(true), ...aus(mapping.speichert) }}>
            <Save size={12} /> {mapping.speichert ? 'Speichert …' : 'Mapping speichern'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function VorlagenEditor({ vorlage, darfSpeichern, onEntwurf, onVerwerfen, onSpeichern }) {
  const geaendert = vorlage.entwurf !== vorlage.html;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: T.warn, fontSize: T.tMikro, fontWeight: 700 }}>
          <AlertTriangle size={12} /> Wirkt auf alle künftigen Teaser – auch auf die Automatik.
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: T.tMikro, color: T.muted }}>
          {vorlage.source === 'gespeichert' ? `Gespeicherte Vorlage${vorlage.updatedAt ? ` · ${zeitText(vorlage.updatedAt)}` : ''}` : 'Originalvorlage'}
        </span>
        {darfSpeichern ? (
          <>
            <button type="button" onClick={onVerwerfen} disabled={!geaendert} style={{ ...textKnopfStil, ...aus(!geaendert) }}>Verwerfen</button>
            <button type="button" onClick={onSpeichern} disabled={!geaendert || vorlage.speichert}
              style={{ ...knopfStil(true), ...aus(!geaendert || vorlage.speichert) }}>
              <Save size={12} /> {vorlage.speichert ? 'Speichert …' : 'Vorlage speichern'}
            </button>
          </>
        ) : null}
      </div>
      <div style={{ fontSize: T.tMikro, color: T.muted, lineHeight: 1.5 }}>
        Platzhalter als <code style={{ fontFamily: 'monospace' }}>{'{{NAME}}'}</code>. Ein Element mit
        {' '}<code style={{ fontFamily: 'monospace' }}>data-teaser-optional=&quot;PACHT&quot;</code>{' '}
        fällt samt Inhalt weg, wenn der Wert leer ist.
      </div>
      <textarea
        value={vorlage.entwurf}
        onChange={(e) => onEntwurf(e.target.value)}
        readOnly={!darfSpeichern}
        spellCheck={false}
        wrap="off"
        aria-label="Vorlage als HTML"
        style={{
          width: '100%', minHeight: 260, resize: 'vertical', padding: '8px 10px', borderRadius: T.rFeld,
          border: `1px solid ${T.rand}`, background: T.ink, color: '#E2E8F0', fontSize: T.tSm,
          fontFamily: 'monospace', lineHeight: 1.5, whiteSpace: 'pre', overflowX: 'auto', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

const LEERE_VORLAGE = { html: '', entwurf: '', source: '', updatedAt: null, laedt: false, fehler: '', speichert: false, geladen: false };
const LEERES_MAPPING = { felder: {}, gespeichertAm: null, laedt: false, fehler: '', speichert: false, geladen: false };

export default function TeaserEinstellungen({
  apiBase, authHeaders, isAuthenticated, canCreate, canEdit, historie, onGeaendert,
}) {
  const [offen, setOffen] = useState({ historie: false, mapping: false, vorlage: false });
  const [vorlage, setVorlage] = useState(LEERE_VORLAGE);
  const [mapping, setMapping] = useState(LEERES_MAPPING);

  // Mapping pflegen darf, wer den Teaser bearbeiten oder erzeugen darf; die
  // Vorlage speichern nur mit canCreate - so prueft es auch der Server.
  const darfMapping = canCreate || canEdit;
  const darfVorlage = canCreate;

  const ladeVorlage = useCallback(async () => {
    setVorlage((v) => ({ ...v, laedt: true, fehler: '' }));
    try {
      const daten = await teaserLesen(apiBase, 'ffpv-teaser-template', authHeaders);
      const html = String(daten?.html || '');
      setVorlage({ ...LEERE_VORLAGE, html, entwurf: html, source: daten?.source || '', updatedAt: daten?.updatedAt || null, geladen: true });
    } catch (fehler) {
      setVorlage((v) => ({ ...v, laedt: false, fehler: `Vorlage nicht ladbar: ${fehler.message}` }));
    }
  }, [apiBase, authHeaders]);

  const ladeMapping = useCallback(async () => {
    setMapping((m) => ({ ...m, laedt: true, fehler: '' }));
    try {
      const { felder, gespeichertAm } = await mappingLaden(authHeaders);
      setMapping({ ...LEERES_MAPPING, felder, gespeichertAm, geladen: true });
    } catch (fehler) {
      setMapping((m) => ({ ...m, laedt: false, fehler: `Mapping nicht ladbar: ${fehler.message}` }));
    }
  }, [authHeaders]);

  const umschalten = (bereich) => {
    const wirdOffen = !offen[bereich];
    setOffen((o) => ({ ...o, [bereich]: wirdOffen }));
    if (!wirdOffen || !isAuthenticated) return;
    // Das Mapping braucht die Platzhalter der Vorlage.
    if ((bereich === 'mapping' || bereich === 'vorlage') && !vorlage.geladen && !vorlage.laedt) ladeVorlage();
    if (bereich === 'mapping' && !mapping.geladen && !mapping.laedt) ladeMapping();
  };

  const speichereMapping = async () => {
    setMapping((m) => ({ ...m, speichert: true, fehler: '' }));
    try {
      await mappingSpeichern(mapping.felder, authHeaders);
      setMapping((m) => ({ ...m, speichert: false, gespeichertAm: new Date().toISOString() }));
      onGeaendert('Mapping gespeichert.');
    } catch (fehler) {
      setMapping((m) => ({ ...m, speichert: false, fehler: `Speichern fehlgeschlagen: ${fehler.message}` }));
    }
  };

  const speichereVorlage = async () => {
    if (!vorlage.entwurf.trim()) {
      setVorlage((v) => ({ ...v, fehler: 'Die Vorlage darf nicht leer sein.' }));
      return;
    }
    setVorlage((v) => ({ ...v, speichert: true, fehler: '' }));
    try {
      const daten = await teaserSenden(apiBase, { action: 'ffpv-teaser-template-save', html: vorlage.entwurf }, authHeaders);
      if (daten?.ok === false) throw new Error(daten?.detail || 'Der Server hat nicht gespeichert.');
      setVorlage((v) => ({
        ...v, speichert: false, html: v.entwurf, source: 'gespeichert', updatedAt: daten?.updatedAt || new Date().toISOString(),
      }));
      onGeaendert('Vorlage gespeichert. Sie gilt ab sofort für alle Teaser.');
    } catch (fehler) {
      setVorlage((v) => ({ ...v, speichert: false, fehler: `Speichern fehlgeschlagen: ${fehler.message}` }));
    }
  };

  const platzhalter = useMemo(() => platzhalterAusHtml(vorlage.entwurf || vorlage.html), [vorlage.entwurf, vorlage.html]);
  const vorlageGeaendert = vorlage.entwurf !== vorlage.html;

  return (
    <div style={{ ...karteStil, padding: 0 }}>
      <div style={{ padding: '12px 14px 10px' }}>
        <Ueberzeile>Hintergrund</Ueberzeile>
      </div>

      <Abschnitt offen={offen.historie} onUmschalten={() => umschalten('historie')} icon={History}
        titel={`Historie${historie.items.length ? ` (${historie.items.length})` : ''}`}>
        <HistorieListe historie={historie} />
      </Abschnitt>

      {darfMapping ? (
        <Abschnitt offen={offen.mapping} onUmschalten={() => umschalten('mapping')} icon={Link2}
          titel={`Mapping${platzhalter.length ? ` (${platzhalter.length} Platzhalter)` : ''}`}>
          {vorlage.laedt ? <Laedt>Vorlage wird geladen …</Laedt> : null}
          {/* Ohne Vorlage keine Platzhalter - der Fehler gehoert auch hierher. */}
          <Fehlerkasten>{vorlage.fehler}</Fehlerkasten>
          <MappingEditor platzhalter={platzhalter} mapping={mapping} darfSpeichern={darfMapping}
            onFeld={(schluessel, wert) => setMapping((m) => ({ ...m, felder: { ...m.felder, [schluessel]: wert } }))}
            onSpeichern={speichereMapping} />
        </Abschnitt>
      ) : null}

      {darfMapping || darfVorlage ? (
        <Abschnitt offen={offen.vorlage} onUmschalten={() => umschalten('vorlage')} icon={Settings2}
          titel="Vorlage (HTML)"
          zusatz={vorlageGeaendert ? <Marke ton="warn">ungespeichert</Marke> : null}>
          {vorlage.laedt ? <Laedt>Vorlage wird geladen …</Laedt> : null}
          <Fehlerkasten>{vorlage.fehler}</Fehlerkasten>
          {vorlage.geladen ? (
            <VorlagenEditor vorlage={vorlage} darfSpeichern={darfVorlage}
              onEntwurf={(html) => setVorlage((v) => ({ ...v, entwurf: html }))}
              onVerwerfen={() => setVorlage((v) => ({ ...v, entwurf: v.html, fehler: '' }))}
              onSpeichern={speichereVorlage} />
          ) : null}
        </Abschnitt>
      ) : null}
    </div>
  );
}
